import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../../shadcn/components/ui/Card";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "../../../shadcn/components/ui/Dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../../shadcn/components/ui/Avatar";
import { Button } from "../../../shadcn/components/ui/Button";
import { Input } from "../../../shadcn/components/ui/Input";
import { Paperclip, Send, MessageCircle } from "lucide-react";
import Pusher from "pusher-js";
import { useNavigate } from "react-router-dom";

const UserGroupChat = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [members, setMembers] = useState("");
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [groupToJoin, setGroupToJoin] = useState(null);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem("authToken");
  const currentUserEmail = localStorage.getItem("email");
  const role = localStorage.getItem("userRole");
  const navigate = useNavigate();

  // Fetch all groups the user is part of
  const fetchGroups = async () => {
    try {
      const response = await axios.post(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/message/public/usergroup",
        { token }
      );
      setGroups(response.data?.groups || []);
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroups([]);
    }
  };

  // Create a new public group
  const createGroup = async (groupName, members) => {
    try {
      const response = await axios.post(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/message/chatroom/create",
        {
          groupName,
          members: members.split(",").map((email) => email.trim()),
          token,
        }
      );
      toast.success("Group created successfully!");
      fetchGroups();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("Group already exists. Navigating to the group...");
        navigate("/message-dashboard");
      } else {
        toast.error("Error creating group");
      }
    }
  };


  // Add user to a group
  
  // Handle chat icon click
  const handleChatIconClick = async (username, userEmail) => {
    const groupName = ` ${username}`;
    const members = `${currentUserEmail},${userEmail}`;
    try {
      await createGroup(groupName, members);
      if (role === "Service Provider") {
        navigate("/servicedashboard/messages");
      } else if (role === "Managed Care Organizations") {
        navigate("/Managed Care Organization's/messages");
      } else if (role === "Individual") {
        navigate("/Dashboard/messages");
      } else if (role === "Property Owner") {
        navigate("/propertyownerdashboard/messages");
      } else if (role === "Non Profits") {
        navigate("/501 C3 Corporations/messages");
      } else if (role === "Real Estate Professionals") {
        navigate("/Hospital/HospitalSystems/messages");
      } else if (role === "SuperAdmin") {
        navigate("/superadmin/messages");
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setGroupToJoin(groupName);
        setShowJoinDialog(true);
      }
    }
  };

  // Handle join group confirmation
  const handleJoinGroup = async () => {
    if (groupToJoin) {
      try {
        
        setShowJoinDialog(false);
        setSelectedGroup(groupToJoin); // Automatically select the group
        fetchMessages(); // Fetch messages for the selected group
      } catch (error) {
        toast.error("Error joining the group");
      }
    }
  };

  // Fetch messages for the selected group
  const fetchMessages = async () => {
    if (!selectedGroup) return;
    try {
      const response = await axios.post(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/message/public/chatroom/get-message",
        { groupName: selectedGroup, token }
      );
      setMessages(response.data?.groupMessages || []);
      scrollToBottom();
    } catch (error) {
      if (error.response?.status === 403 || error.response?.data?.message === "You are not a member of this group") {
        // Open the dialog to ask if the user wants to join the group
        setGroupToJoin(selectedGroup);
        setShowJoinDialog(true);
      } else {
        console.error("Error fetching messages:", error);
        setMessages([]);
      }
    }
  };

  // Scroll to the bottom of the chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Send a message
  const sendMessage = async () => {
    const stringifiedToken = localStorage.getItem("authToken");
    const token = JSON.stringify(stringifiedToken);
    if (!selectedGroup) return;
    const formData = new FormData();
    formData.append("token", token);
    formData.append("textMessage", newMessage);
    formData.append("groupName", selectedGroup);
    if (selectedFile) {
      formData.append("imageMessage", selectedFile[0]);
    }

    try {
      await axios.post(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/message/public/chatroom/sendmessage",
        formData
      );
      setNewMessage("");
      setSelectedFile(null);
      setImagePreview(null);
      fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(e.target.files);
      setImagePreview([URL.createObjectURL(file)]);
    }
  };

  // Fetch user details
  const handleClick = async (email) => {
    try {
      const response = await axios.get(
        `https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/get-userdata-email?email=${email}`
      );
      if (response.data) {
        setSelectedUser(response.data);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  // Fetch groups when the component mounts. Intentionally mount-only: fetchGroups
  // only reads the stable auth token, and create/join handlers re-call it directly.
  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pusher integration for real-time messaging
  useEffect(() => {
    if (selectedGroup) {
      const pusher = new Pusher("e9cffd90dff19b3b8976", {
        cluster: "ap2",
      });

      const sanitizedGroupName = selectedGroup.replace(/ /g, "");
      const channel = pusher.subscribe(sanitizedGroupName);

      channel.bind("send-message", (data) => {
        const { message } = data;
        if (!message || !message.textMessage || !message.sender || !message.date) {
          console.error("Received message is missing required fields");
          return;
        }

        setMessages((prevMessages) => [
          ...prevMessages,
          {
            textMessage: message.textMessage,
            sender: message.sender,
            date: message.date,
            imageMessage: message.imageMessage || null,
          },
        ]);
        scrollToBottom();
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
      };
    }
  }, [selectedGroup]);

  // Fetch messages when the selected group changes. fetchMessages is redefined each
  // render and reads selectedGroup directly, so selectedGroup is the only real trigger.
  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-100 font-sans">
      <ToastContainer />
      {/* Sidebar */}
      <div className="w-full md:w-1/4 bg-purple-800 text-white p-6 overflow-y-auto no-scrollbar">
        
        <h3 className="text-xl font-bold mb-4">Public Groups</h3>
        <ul className="space-y-2">
          {groups?.map((group, index) => (
            <li
              key={index}
              onClick={() => setSelectedGroup(group.groupName)}
              className="p-3 bg-purple-700 rounded-lg cursor-pointer hover:bg-purple-600"
            >
              {group.groupName}
            </li>
          ))}
        </ul>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 p-6 bg-white">
        {selectedGroup ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex gap-2">
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.75 11.25H21.25M8.75 16.25H21.25M26.25 25L22.0946 22.9222C21.7796 22.7647 21.6221 22.686 21.457 22.6305C21.3105 22.5812 21.1596 22.5456 21.0065 22.5241C20.834 22.5 20.6579 22.5 20.3058 22.5H7.75C6.34986 22.5 5.6498 22.5 5.11503 22.2275C4.64461 21.9879 4.26216 21.6054 4.02249 21.135C3.75 20.6002 3.75 19.9001 3.75 18.5V9C3.75 7.59986 3.75 6.8998 4.02249 6.36502C4.26216 5.89461 4.64461 5.51216 5.11503 5.27249C5.6498 5 6.34987 5 7.75 5H22.25C23.6501 5 24.3503 5 24.885 5.27249C25.3554 5.51216 25.7379 5.89461 25.9775 6.36502C26.25 6.8998 26.25 7.59987 26.25 9V25Z"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Live Chat
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col">
              {/* Messages Area */}
              <div className="flex-1 max-h-80 overflow-y-auto no-scrollbar space-y-4 pb-6">
                {selectedGroup &&
                  messages.map((message) => (
                    <div
                      key={message._id || message.date}
                      className={`flex items-start cursor-pointer gap-3 p-3 ${
                        message.sender === currentUserEmail ? "justify-end" : "justify-start"
                      }`}
                    >
                     
                      <div className="flex-1 max-w-xs">
                        <div
                          className={`p-3 rounded-lg ${
                            message.sender === currentUserEmail
                              ? "bg-purple-700 text-white ml-auto"
                              : "bg-gray-200 text-gray-800 mr-auto"
                          }`}
                        >
                          <h3 className="font-medium text-sm">
                            {message.sender === currentUserEmail ? "You" : message.sender}
                          </h3>
                          {message.textMessage && <p className="text-sm">{message.textMessage}</p>}
                          {message.imageMessage &&
                            Array.isArray(message.imageMessage) &&
                            message.imageMessage.map((imgUrl, index) => (
                              <img
                                key={index}
                                src={imgUrl}
                                alt={`Uploaded ${index + 1}`}
                                className="mt-2 rounded-lg max-w-full h-auto"
                              />
                            ))}
                          <span className="text-xs text-gray-400">
                            {new Date(message.date).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="mt-4 flex flex-col gap-3 pt-4 border-t border-gray-300">
                {imagePreview && (
                  <div className="relative">
                    {imagePreview.map((preview, index) => (
                      <div key={index} className="relative">
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-24 h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => {
                            setImagePreview(null);
                            setSelectedFile(null);
                          }}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-3">
                  <label htmlFor="file-input" className="cursor-pointer">
                    <Paperclip className="text-gray-500 hover:text-gray-700" />
                  </label>
                  <input
                    id="file-input"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message"
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={!newMessage.trim() && !selectedFile}>
                    <Send />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <p className="text-gray-500">Select a group to start chatting</p>
        )}
      </div>

      {/* Join Group Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
      <div className="flex items-center justify-center ">
        <DialogContent className="bg-white p-6 rounded-lg shadow-xl w-96 transition-transform transform scale-100">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-800 text-center">Join Group</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-gray-600 text-center mt-2">
            You are not a member of this group. Do you want to join?
          </DialogDescription>
          <DialogFooter className="flex justify-between mt-4">
            <Button onClick={() => setShowJoinDialog(false)} className="bg-gray-200 text-gray-700 hover:bg-gray-300 px-4 py-2 rounded-md">
              Cancel
            </Button>
            <Button onClick={handleJoinGroup} className="bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-2 rounded-md">
              Join
            </Button>
          </DialogFooter>
        </DialogContent>
      </div>
    </Dialog>
    </div>
  );
};

export default UserGroupChat;